import type {
  ScheduleFrequency,
  DailyScheduleType,
  WeekDay,
  MonthlyWeek,
  MonthlyDay,
  ReportPeriod,
  MonthlyScheduleType,
  RelativeWeek,
  RelativeDay,
} from "./types"

// Helper to get user's timezone
export const getUserTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

// Frequency options
export const FREQUENCY_OPTIONS: { value: ScheduleFrequency; label: string }[] = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
]

// Daily schedule type options (aligned with API)
export const DAILY_SCHEDULE_OPTIONS: { value: DailyScheduleType; label: string }[] = [
  { value: "every_day", label: "Every day" },
  { value: "weekdays_only", label: "Weekdays only" },
]

// Weekly day options
export const WEEKDAY_OPTIONS: { value: WeekDay; label: string; shortLabel: string }[] = [
  { value: "mon", label: "Monday", shortLabel: "M" },
  { value: "tue", label: "Tuesday", shortLabel: "T" },
  { value: "wed", label: "Wednesday", shortLabel: "W" },
  { value: "thu", label: "Thursday", shortLabel: "T" },
  { value: "fri", label: "Friday", shortLabel: "F" },
  { value: "sat", label: "Saturday", shortLabel: "S" },
  { value: "sun", label: "Sunday", shortLabel: "S" },
]

// Monthly schedule type options (for form UI - legacy format)
export const MONTHLY_SCHEDULE_TYPE_OPTIONS: { value: MonthlyScheduleType; label: string }[] = [
  { value: "specific_day", label: "On a specific date" },
  { value: "relative_day", label: "On a relative day" },
]

// Monthly specific day options (1-28 to avoid issues with month lengths)
export const MONTHLY_DAY_OPTIONS: { value: number; label: string }[] = Array.from(
  { length: 28 },
  (_, i) => ({
    value: i + 1,
    label: `${i + 1}${getOrdinalSuffix(i + 1)}`,
  })
)

// Relative week options (aligned with API)
export const RELATIVE_WEEK_OPTIONS: { value: RelativeWeek; label: string }[] = [
  { value: "first", label: "First" },
  { value: "second", label: "Second" },
  { value: "third", label: "Third" },
  { value: "fourth", label: "Fourth" },
  { value: "last", label: "Last" },
]

// Relative day options (legacy format for form UI)
export const RELATIVE_DAY_OPTIONS: { value: RelativeDay; label: string }[] = [
  { value: "monday", label: "Monday" },
  { value: "tuesday", label: "Tuesday" },
  { value: "wednesday", label: "Wednesday" },
  { value: "thursday", label: "Thursday" },
  { value: "friday", label: "Friday" },
  { value: "last_business_day", label: "Business day" },
]

// Monthly day options (aligned with API monthly_day enum)
export const MONTHLY_DAY_NAME_OPTIONS: { value: MonthlyDay; label: string }[] = [
  { value: "monday", label: "Monday" },
  { value: "tuesday", label: "Tuesday" },
  { value: "wednesday", label: "Wednesday" },
  { value: "thursday", label: "Thursday" },
  { value: "friday", label: "Friday" },
  { value: "saturday", label: "Saturday" },
  { value: "sunday", label: "Sunday" },
  { value: "business_day", label: "Business day" },
]

// Monthly week options (aligned with API monthly_week enum)
export const MONTHLY_WEEK_OPTIONS: { value: MonthlyWeek; label: string }[] = [
  { value: "first", label: "First" },
  { value: "second", label: "Second" },
  { value: "third", label: "Third" },
  { value: "fourth", label: "Fourth" },
  { value: "last", label: "Last" },
]

// Report period options (aligned with API report_period enum)
export const REPORT_PERIOD_OPTIONS: { value: ReportPeriod; label: string }[] = [
  { value: "last_7_days", label: "Last 7 days" },
  { value: "last_14_days", label: "Last 14 days" },
  { value: "last_30_days", label: "Last 30 days" },
  { value: "last_90_days", label: "Last 90 days" },
  { value: "this_month", label: "This month" },
  { value: "last_month", label: "Last month" },
]

// Time options (full hours only, value is the hour number)
export const TIME_OPTIONS: { value: number; label: string }[] = Array.from(
  { length: 24 },
  (_, i) => {
    const period = i >= 12 ? "PM" : "AM"
    const displayHours = i === 0 ? 12 : i > 12 ? i - 12 : i
    const label = `${displayHours}:00 ${period}`
    return { value: i, label }
  }
)

// Legacy TIME_OPTIONS format (for backward compatibility during migration)
export const TIME_OPTIONS_STRING: { value: string; label: string }[] = Array.from(
  { length: 24 },
  (_, i) => {
    const value = `${i.toString().padStart(2, "0")}:00`
    const period = i >= 12 ? "PM" : "AM"
    const displayHours = i === 0 ? 12 : i > 12 ? i - 12 : i
    const label = `${displayHours}:00 ${period}`
    return { value, label }
  }
)

// Common timezone options
export const TIMEZONE_OPTIONS: { value: string; label: string }[] = [
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { value: "America/Phoenix", label: "Phoenix (MST)" },
  { value: "Europe/London", label: "London (GMT/BST)" },
  { value: "Europe/Paris", label: "Paris (CET)" },
  { value: "Europe/Berlin", label: "Berlin (CET)" },
  { value: "Asia/Tokyo", label: "Tokyo (JST)" },
  { value: "Asia/Shanghai", label: "Shanghai (CST)" },
  { value: "Asia/Singapore", label: "Singapore (SGT)" },
  { value: "Australia/Sydney", label: "Sydney (AEST)" },
  { value: "UTC", label: "UTC" },
]

// Helper function for ordinal suffixes
function getOrdinalSuffix(n: number): string {
  const s = ["th", "st", "nd", "rd"]
  const v = n % 100
  return s[(v - 20) % 10] || s[v] || s[0]
}

// Helper to convert legacy relative day to API format
export function legacyRelativeDayToApi(relativeDay: RelativeDay): MonthlyDay {
  if (relativeDay === "last_business_day") {
    return "business_day"
  }
  return relativeDay as MonthlyDay
}

// Helper to convert API monthly day to legacy format
export function apiMonthlyDayToLegacy(monthlyDay: MonthlyDay): RelativeDay {
  if (monthlyDay === "business_day") {
    return "last_business_day"
  }
  if (monthlyDay === "saturday" || monthlyDay === "sunday") {
    // These don't exist in legacy format, default to friday
    return "friday"
  }
  return monthlyDay as RelativeDay
}
